/**
 * DiscrepancyModal (Reconciliation Detail)
 * ========================================
 * Displays detailed line-by-line reconciliation between Quote and Invoice.
 * Shows matched items (Green) and discrepancies (Red/Yellow).
 */

import React from 'react';
import { MatchResult } from '..'; // Importing from index.tsx in parent folder
import './DiscrepancyModal.scss';

// Updated Interfaces matching new Backend Response
interface ReconciliationItem {
    line_id: string;
    item_description: string;
    quote_item: {
        price: number;
        quantity: number;
        total: number;
    } | null;
    invoice_item: {
        price: number;
        quantity: number;
        total: number;
    } | null;
    status: 'match' | 'price_mismatch' | 'quantity_mismatch' | 'missing_in_invoice' | 'extra_in_invoice';
    difference_amount: number;
    gpt_comment: string;
}

interface VerificationSummary {
    total_items: number;
    matched_items: number;
    discrepancy_items: number;
    quote_total: number;
    invoice_total: number;
    difference_amount: number;
}

interface VerificationResult {
    match_reason?: string;
    // Support both old (discrepancies) and new (reconciliation_lines) formats for backward compatibility
    discrepancies?: any[];
    reconciliation_lines?: ReconciliationItem[];
    summary?: VerificationSummary;
    field_analysis?: {
        vendor: { status: string; score: number; comment: string };
        invoice_date: { status: string; score: number; comment: string };
        total_amount: { status: string; score: number; comment: string };
    };
}

interface DiscrepancyModalProps {
    match: MatchResult;
    onClose: () => void;
}

const DiscrepancyModal: React.FC<DiscrepancyModalProps> = ({ match, onClose }) => {
    // Determine header status color
    const getHeaderStatusColor = (status: string) => {
        if (status === 'matched') return '#10B981'; // Green
        if (status === 'partial') return '#F59E0B'; // Yellow
        if (status === 'discrepancy') return '#EF4444'; // Red
        return '#6B7280'; // Gray
    };

    const verification = match.verification as VerificationResult;

    // Helper to format currency
    const formatCurrency = (val?: number) => {
        if (val === undefined || val === null) return '-';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    };

    return (
        <div className="discrepancy-modal">
            <div className="discrepancy-modal__content" style={{ maxWidth: '1000px' }}>
                {/* Header */}
                <div className="discrepancy-modal__header">
                    <div>
                        <h2>Reconciliation Details</h2>
                        <div className="discrepancy-modal__info">
                            <span>
                                Invoice: <strong>{match.invoiceFile}</strong>
                            </span>
                            <span>
                                Quote: <strong>{match.quoteFile || 'No match'}</strong>
                            </span>
                        </div>
                    </div>

                    <div>
                        <div className="discrepancy-modal__header-right">
                            <span
                                className="discrepancy-modal__status-badge"
                                style={{
                                    backgroundColor: `${getHeaderStatusColor(match.status)}20`,
                                    color: getHeaderStatusColor(match.status)
                                }}
                            >
                                {match.status.toUpperCase()}
                            </span>
                            <div className="discrepancy-modal__confidence">
                                {match.confidence}% Confidence
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="discrepancy-modal__close-btn"
                    >
                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="discrepancy-modal__body">
                    {!verification ? (
                        <div className="discrepancy-modal__empty">
                            <p>No verification details available.</p>
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            {verification.summary && (
                                <div className="discrepancy-modal__summary">
                                    <div className="discrepancy-modal__summary-item">
                                        <div className="discrepancy-modal__summary-item-label">Quote Total</div>
                                        <div className="discrepancy-modal__summary-item-value">
                                            {formatCurrency(verification.summary.quote_total)}
                                        </div>
                                    </div>
                                    <div className="discrepancy-modal__summary-item">
                                        <div className="discrepancy-modal__summary-item-label">Invoice Total</div>
                                        <div className="discrepancy-modal__summary-item-value">
                                            {formatCurrency(verification.summary.invoice_total)}
                                        </div>
                                    </div>
                                    <div className="discrepancy-modal__summary-item">
                                        <div className="discrepancy-modal__summary-item-label">Difference</div>
                                        <div className={`discrepancy-modal__summary-item-value ${verification.summary.difference_amount && Math.abs(verification.summary.difference_amount) > 0.01 ? 'negative' : 'positive'}`}>
                                            {formatCurrency(verification.summary.difference_amount)}
                                        </div>
                                    </div>
                                    <div className="discrepancy-modal__summary-item">
                                        <div className="discrepancy-modal__summary-item-label">Matched Items</div>
                                        <div className="discrepancy-modal__summary-item-value positive">
                                            {verification.summary.matched_items || 0} / {verification.summary.total_items || 0}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* GPT Match Reason */}
                            {verification.match_reason && (
                                <div className="discrepancy-modal__analysis">
                                    <strong>AI Analysis:</strong> {verification.match_reason}
                                </div>
                            )}

                            {/* NEW: Field Level Analysis */}
                            {verification.field_analysis && (
                                <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                                    {/* Vendor Analysis */}
                                    <div className={`p-4 rounded-lg border ${verification.field_analysis.vendor.status === 'match' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                        <div className="text-sm font-semibold text-gray-700 mb-1">Vendor Check</div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`status-indicator ${verification.field_analysis.vendor.status === 'match' ? 'match' : 'mismatch'}`}></span>
                                            <span className="font-bold">{Math.round(verification.field_analysis.vendor.score * 100)}%</span>
                                        </div>
                                        <div className="text-xs text-gray-600">{verification.field_analysis.vendor.comment}</div>
                                    </div>

                                    {/* Date Analysis */}
                                    <div className={`p-4 rounded-lg border ${verification.field_analysis.invoice_date.status === 'match' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                        <div className="text-sm font-semibold text-gray-700 mb-1">Date Consistency</div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`status-indicator ${verification.field_analysis.invoice_date.status === 'match' ? 'match' : 'mismatch'}`}></span>
                                            <span className="font-bold">{Math.round(verification.field_analysis.invoice_date.score * 100)}%</span>
                                        </div>
                                        <div className="text-xs text-gray-600">{verification.field_analysis.invoice_date.comment}</div>
                                    </div>

                                    {/* Financial Analysis */}
                                    <div className={`p-4 rounded-lg border ${verification.field_analysis.total_amount.status === 'match' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                        <div className="text-sm font-semibold text-gray-700 mb-1">Total Amount</div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`status-indicator ${verification.field_analysis.total_amount.status === 'match' ? 'match' : 'mismatch'}`}></span>
                                            <span className="font-bold">{Math.round(verification.field_analysis.total_amount.score * 100)}%</span>
                                        </div>
                                        <div className="text-xs text-gray-600">{verification.field_analysis.total_amount.comment}</div>
                                    </div>
                                </div>
                            )}

                            {/* Full Reconciliation Table */}
                            {verification.reconciliation_lines && verification.reconciliation_lines.length > 0 ? (
                                <div className="discrepancy-modal__table-wrapper">
                                    <table className="discrepancy-modal__table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '30%' }}>Item / Description</th>
                                                <th className="text-right border-l" style={{ width: '20%' }}>Quote Value</th>
                                                <th className="text-right" style={{ width: '20%' }}>Invoice Value</th>
                                                <th className="text-center border-l" style={{ width: '30%' }}>GPT Result</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {verification.reconciliation_lines.map((line: ReconciliationItem, idx: number) => {
                                                const isMatch = line.status === 'match';

                                                return (
                                                    <tr key={idx} className={isMatch ? 'bg-green-50' : 'bg-red-50'}>
                                                        <td className="font-medium">
                                                            {line.item_description}
                                                        </td>
                                                        <td className="text-right border-l text-gray">
                                                            {line.quote_item ? (
                                                                <div>
                                                                    <div>{formatCurrency(line.quote_item.total)}</div>
                                                                    <div className="text-xs text-secondary">
                                                                        {line.quote_item.quantity} x {formatCurrency(line.quote_item.price)}
                                                                    </div>
                                                                </div>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="text-right">
                                                            {line.invoice_item ? (
                                                                <div>
                                                                    <div className={!isMatch ? 'text-red font-bold' : ''}>
                                                                        {formatCurrency(line.invoice_item.total)}
                                                                    </div>
                                                                    <div className="text-xs text-secondary">
                                                                        {line.invoice_item.quantity} x {formatCurrency(line.invoice_item.price)}
                                                                    </div>
                                                                </div>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="text-center border-l">
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                                <span className={`badge ${isMatch ? 'bg-green text-green' : 'bg-red text-red'}`}>
                                                                    {isMatch ? '🟢 MATCHED' : '🔴 MISMATCH'}
                                                                </span>
                                                            </div>
                                                            {line.gpt_comment && (
                                                                <div className="text-xs mt-1 text-secondary" style={{ fontStyle: 'italic' }}>
                                                                    "{line.gpt_comment}"
                                                                </div>
                                                            )}
                                                            {!isMatch && line.difference_amount !== 0 && (
                                                                <div className="text-xs mt-1 text-red font-bold">
                                                                    Diff: {formatCurrency(line.difference_amount)}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                /* Fallback for legacy specific discrepancies list if reconciliation lines missing */
                                verification.discrepancies ? (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                        <p>Legacy format data. Please re-run analysis to see full reconciliation table.</p>
                                    </div>
                                ) : (
                                    <div className="discrepancy-modal__empty">
                                        <p>No item details available.</p>
                                    </div>
                                )
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="discrepancy-modal__footer">
                    <button onClick={onClose}>Close</button>
                    <button>Download Report</button>
                </div>
            </div>
        </div>
    );
};

export default DiscrepancyModal;
